import React, { useEffect, useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import { useQuery } from "@apollo/react-hooks";
import { withUser } from "../Contexts/user";
import queries from "../helpers/queries";

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const Announcements = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [hasAutoOpened, setHasAutoOpened] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const { data: announcementsData } = useQuery(queries.FETCH_ANNOUNCEMENTS, {
        variables: {
            limit: 100,
            page: 1,
            status: "active",
        },
        fetchPolicy: "network-only",
    });

    const announcements = useMemo(
        () =>
            (announcementsData?.fetchAnnouncements?.data || []).slice().sort((a, b) => {
                return (a?.position || 0) - (b?.position || 0);
            }),
        [announcementsData]
    );

    useEffect(() => {
        if (!hasAutoOpened && announcements.length > 0) {
            setSelectedAnnouncement(announcements[0]);
            setShowModal(true);
            setHasAutoOpened(true);
        }
    }, [announcements, hasAutoOpened]);

    const selectedIndex = useMemo(
        () =>
            announcements.findIndex(
                (a) => a._id === selectedAnnouncement?._id
            ),
        [announcements, selectedAnnouncement]
    );

    const showPreviousAnnouncement = (e) => {
        e.stopPropagation();
        if (announcements.length === 0 || selectedIndex < 0) return;
        const prev = selectedIndex === 0 ? announcements.length - 1 : selectedIndex - 1;
        setSelectedAnnouncement(announcements[prev]);
    };

    const showNextAnnouncement = (e) => {
        e.stopPropagation();
        if (announcements.length === 0 || selectedIndex < 0) return;
        const next = selectedIndex === announcements.length - 1 ? 0 : selectedIndex + 1;
        setSelectedAnnouncement(announcements[next]);
    };

    const getContentLines = (value) =>
        (value || "")
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean);

    return (
        <div className="campaigns-page">
            <div className="campaigns-container">
                <h1 className="campaigns-title">Management Announcements</h1>
                <p className="text-sm text-gray-400 mb-4">
                    Official updates and important notices from D&D management.
                </p>
                <div className="overflow-x-auto bg-white rounded shadow">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <th className="hidden md:table-cell py-3 px-4 text-sm font-semibold text-gray-500">
                                    
                                </th>
                                <th className="py-3 px-4 text-sm font-semibold text-gray-500">
                                    Title
                                </th>
                                <th className="hidden md:table-cell py-3 px-4 text-sm font-semibold text-gray-500">
                                    Created Date
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {announcements.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={3}
                                        className="py-6 px-4 text-center text-gray-400 text-sm"
                                    >
                                        No announcements found.
                                    </td>
                                </tr>
                            )}
                            {announcements.map((announcement, index) => (
                                <tr
                                    key={announcement._id}
                                    className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                                >
                                    <td className="hidden md:table-cell py-3 px-4 text-sm text-gray-600">
                                        {index + 1}
                                    </td>
                                    <td className="py-3 px-4 text-sm text-black">
                                        <span className="mr-3">
                                            {announcement.title || `Announcement #${announcement.position}`}
                                        </span>
                                        <button
                                            className="bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium py-1 px-3 rounded transition-colors"
                                            onClick={() => {
                                                setSelectedAnnouncement(announcement);
                                                setShowModal(true);
                                            }}
                                        >
                                            View
                                        </button>
                                    </td>
                                    <td className="hidden md:table-cell py-3 px-4 text-sm text-gray-600">
                                        {formatDate(announcement.createdAt)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && selectedAnnouncement && (
                <div
                    className="campaign-modal-overlay"
                    onClick={() => setShowModal(false)}
                >
                    <button
                        type="button"
                        onClick={showPreviousAnnouncement}
                        className="text-white hover:text-yellow-300 transition-colors"
                        style={{
                            position: "fixed",
                            left: "24px",
                            top: isMobile ? "40%" : "50%",
                            transform: "translateY(-50%)",
                            zIndex: 60,
                            fontSize: "30px",
                            backgroundColor: "rgba(0, 0, 0, 0.65)",
                            border: "2px solid rgba(255, 255, 255, 0.75)",
                            borderRadius: "9999px",
                            width: "56px",
                            height: "56px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <FiChevronLeft />
                    </button>
                    <div
                        className="campaign-modal-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="campaign-modal-header">
                            <h2 className="campaign-modal-title">
                                {selectedAnnouncement.title || `Announcement #${selectedAnnouncement.position}`}
                            </h2>
                            <button
                                className="campaign-modal-close"
                                onClick={() => setShowModal(false)}
                            >
                                <FiX />
                            </button>
                        </div>
                        <div className="campaign-modal-body">
                            <div className="campaign-modal-details">
                                <div className="campaign-modal-notes">
                                    {getContentLines(
                                        selectedAnnouncement.en_content
                                    ).map((note, index) => (
                                        <p key={`en-${index}`}>{note}</p>
                                    ))}
                                    {getContentLines(
                                        selectedAnnouncement.cn_content
                                    ).map((note, index) => (
                                        <p key={`cn-${index}`}>{note}</p>
                                    ))}
                                </div>
                                <p className="text-sm text-gray-400 mt-4">
                                    Terms & conditions apply.
                                </p>
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={showNextAnnouncement}
                        className="text-white hover:text-yellow-300 transition-colors"
                        style={{
                            position: "fixed",
                            right: "24px",
                            top: isMobile ? "40%" : "50%",
                            transform: "translateY(-50%)",
                            zIndex: 60,
                            fontSize: "30px",
                            backgroundColor: "rgba(0, 0, 0, 0.65)",
                            border: "2px solid rgba(255, 255, 255, 0.75)",
                            borderRadius: "9999px",
                            width: "56px",
                            height: "56px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <FiChevronRight />
                    </button>
                </div>
            )}
        </div>
    );
};

export default withUser(Announcements);
